<script setup>
import {
    onMounted,
    watch,
} from 'vue';
import {
    useRoute, useRouter,
} from 'vue-router';
import {
    storeToRefs,
} from 'pinia';

import AppBusinessCard from '../organisms/app/AppBusinessCard';
import CardFaqBanner from '../organisms/card/CardFaqBanner';
import CardJobMatching from '../organisms/card/CardJobMatching';
import CardLatestApplications from '../organisms/card/CardLatestApplications';
import CardLatestCompanyFollows from '../organisms/card/CardLatestCompanyFollows';
import CardLatestJobAlarm from '../organisms/card/CardLatestJobAlarm';
import CardLatestJobWatchlist from '../organisms/card/CardLatestJobWatchlist';
import CardLatestMessages from '../organisms/card/CardLatestMessages';
import CardLatestProfileViews from '../organisms/card/CardLatestProfileViews';
import FormJobSearch from '../organisms/form/FormJobSearch';
import LayoutDefault from '../layouts/LayoutDefault';
import MinitasksContainer from '../organisms/minitask/MinitasksContainer';
import ProgressLoadingBar from '../atoms/progress/ProgressLoadingBar';
import SliderBlogArticle from '../organisms/slider/SliderBlogArticle';
import TextHeadline from '../atoms/text/TextHeadline';
import ModalCvBooster from '../organisms/modal/ModalCvBooster';
import {
    showSnackbar,
} from '../../utils/snackbar';
import {
    useModalStore,
} from '../../stores/modal-store';
import {
    useProfileStore,
} from '../../stores/profile-store';

const profile = useProfileStore();
const {
    exists: hasProfile,
    fetched,
    hasSeenBooster,
} = storeToRefs(profile);

const router = useRouter();
const route = useRoute();

const modal = useModalStore();

const showUserFeedback = () => {
    if (route.query.watchlistAdded) {
        showSnackbar({
            text: 'Der Job wurde gemerkt.',
            icon: 'check',
        });
        router.replace('/dashboard');
    }
    if (route.query.toast) {
        showSnackbar({ text: route.query.toast });
        router.replace('/dashboard');
    }
};

const isBoosterOutdated = (days) => {
    const compareDate = new Date(new Date().setDate(new Date().getDate() - days));
    return new Date(hasSeenBooster.value).valueOf() <= compareDate.valueOf();
};

const goToForm = ({ id = null, hasNewForm = false }) => {
    router.push({ name: 'page-cv', params: { formId: id, newForm: hasNewForm } });
};

const DAYS_SINCE_KABOOM = 7;
const handleModalCvBooster = () => {
    if (fetched.value && !hasProfile.value && isBoosterOutdated(DAYS_SINCE_KABOOM)) {
        modal.show({ component: ModalCvBooster });
        profile.updateHasSeenBooster({ hasSeenBooster: Date.now() });
    }
};

watch(fetched, handleModalCvBooster, { immediate: true });

onMounted(showUserFeedback);
</script>
<template>
    <LayoutDefault>
        <div class="c-pageDashboard">
            <ProgressLoadingBar/>
            <FormJobSearch data-qa="job search"/>
            <div class="c-pageDashboard__wrap u-clearfix">
                <TextHeadline
                    :level="1"
                    variant="lg-400"
                    class="c-pageDashboard__headline"
                    data-qa="dashboard headline"
                >
                    Übersicht über mein Profil
                </TextHeadline>
                <MinitasksContainer
                    data-qa="minitasks"
                    ga-prefix="DB"
                    @add="goToForm"
                />
                <div class="c-pageDashboard__layout">
                    <aside class="c-pageDashboard__sidebar k-o-ladder k-o-ladder--l">
                        <AppBusinessCard
                            class="k-o-ladder__rung"
                            data-qa="cv box"
                            @complete="goToForm"
                        />
                        <CardLatestProfileViews
                            v-if="hasProfile"
                            class="k-o-ladder__rung"
                            data-qa="profile views card"
                        />
                        <CardLatestMessages
                            v-if="hasProfile"
                            class="k-o-ladder__rung"
                            data-qa="messages card"
                        />
                        <CardFaqBanner
                            class="k-o-ladder__rung"
                            data-qa="faq card"
                            data-gtm-element="DB: FAQ Box"
                            data-gtm-element-detail="Click"
                        />
                    </aside>
                    <div class="c-pageDashboard__content k-o-ladder k-o-ladder--l">
                        <CardJobMatching
                            v-if="hasProfile"
                            ref="jobmatchingCard"
                            class="k-o-ladder__rung"
                            data-qa="job matching card"
                        />
                        <CardLatestJobAlarm
                            class="k-o-ladder__rung"
                            data-qa="job alarm card"
                        />
                        <CardLatestApplications
                            class="k-o-ladder__rung"
                            data-qa="applications card"
                            :has-profile="hasProfile"
                        />
                        <CardLatestJobWatchlist
                            class="k-o-ladder__rung"
                            data-qa="job watchlist card"
                        />
                        <CardLatestCompanyFollows
                            class="k-o-ladder__rung"
                            data-qa="company follow card"
                        />
                    </div>
                    <div class="c-pageDashboard__fullContent">
                        <TextHeadline
                            class="c-pageDashboard__blogHeadline"
                            :level="2"
                            variant="lg-400"
                        >
                            karriere.blog
                        </TextHeadline>
                        <SliderBlogArticle
                            data-gtm-element="DB: Blog Article"
                            data-gtm-element-detail="Open Detail"
                        />
                    </div>
                </div>
            </div>
        </div>
    </LayoutDefault>
</template>

<style lang="scss">
@use 'sass:math';

@import '../../assets/scss/settings/breakpoint';
@import '../../assets/scss/settings/size';
@import '../../assets/scss/settings/spacing';
@import '~@karriereat/global-styles/scss/tools/layout';
@import '../../assets/scss/tools/wrap';

// 1. Prevent the element from taking more space than available.
//    This is necessary for rendering an ellipsis text overflow.
.c-pageDashboard {
    $app-banner-height: 180px;

    &__wrap {
        @include wrap(math.div(10, 12));

        max-width: $k-size-wrap-max-width;
    }

    &__layout {
        @include k-layout($k-spacing--m, $k-spacing--m);

        margin-top: $k-spacing--2xs;

        @media (min-width: $k-breakpoint--m) {
            @include k-layout($k-spacing--l, $k-spacing--l);

            margin-top: $k-spacing--2xs;
        }

        @media (min-width: $k-breakpoint--l) {
            @include k-layout($k-spacing--xl, $k-spacing--xl);

            margin-top: $k-spacing--2xs;
        }

        /* stylelint-disable-next-line custom-property-pattern */
        .k-o-ladder:not(.c-appCompanyMediaBox__badges) > .k-o-ladder__rung:not(:first-child) {
            margin-top: $k-spacing--m;

            @media (min-width: $k-breakpoint--m) {
                margin-top: $k-spacing--l;
            }

            @media (min-width: $k-breakpoint--l) {
                margin-top: $k-spacing--xl;
            }
        }
    }

    &__headline {
        margin-top: $k-spacing--l + $k-spacing--m;
    }

    &__sidebar {
        @include k-layout__item(auto, 19em);

        min-width: 0; // 1
    }

    &__content {
        @include k-layout__item(max, 30em);

        min-width: 0; // 1
    }

    &__fullContent {
        @include k-layout__item(auto, 28em);

        overflow: hidden;
        margin-top: $k-spacing--3xl;
    }

    &__blogHeadline {
        margin-bottom: $k-spacing--2xl;
    }
}
</style>
